/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {Switch, Popover, Text} from '@mantine/core';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import UserAgentGuidelines from 'llm/chat-library/agent-guidelines/UserAgentGuidelines';
import useUserAgentGuidelines from 'llm/chat-library/agent-guidelines/useUserAgentGuidelines';
import {
  DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY,
  DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED,
} from 'llm/deep-research/DeepResearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredSpinner} from 'ui-library/Spinner';

export function AISettingsContent({chatId}: {chatId: string | null}): JSX.Element {
  const [emailNotificationEnabledPref, setEmailNotificationEnabled] = usePreference<boolean | undefined>(
    DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY
  );
  const emailNotificationEnabled =
    emailNotificationEnabledPref ?? DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED;

  const {guidelines, isLoading, saveGuidelines} = useUserAgentGuidelines();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '280px',
        maxWidth: '400px',
        padding: '4px',
        paddingBottom: '8px',
      }}
    >
      {/* Response Preferences Section */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        <h4>Response preferences</h4>
        <Text fw="normal">
          What personal preferences should be considered in Benchling AI responses? This will apply to all
          conversations.
        </Text>
        {isLoading || guidelines == null ? (
          <CenteredSpinner />
        ) : (
          <UserAgentGuidelines value={guidelines} onSave={saveGuidelines} />
        )}
      </div>

      {/* Divider */}
      <div style={{borderTop: '1px solid #e0e0e0', margin: '0 -4px'}} />

      {/* Notifications Section */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
        <h4>Notifications</h4>
        <Switch
          checked={emailNotificationEnabled}
          onChange={(event) => {
            setEmailNotificationEnabled(event.currentTarget.checked);
          }}
          labelPosition="left"
          label={<Text fw="normal">Notify me by email when Deep Research reports complete</Text>}
        />
      </div>

      {chatId && (
        <>
          {/* Divider */}
          <div style={{borderTop: '1px solid #e0e0e0', margin: '0 -4px'}} />

          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="audit-trail"
            onClick={() => {
              AuditActions.exportAuditLogs([chatId]);
            }}
            size={ButtonSize.SMALL}
            text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
            data-test-component="AISidepanelV2"
            data-test-element="exportAuditLogButton"
          />
        </>
      )}
    </div>
  );
}

export function AISettingsToggleButton({
  size,
  chatId,
}: {
  size: ButtonSize;
  chatId: string | null;
}): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  return (
    <Popover
      position="bottom-end"
      shadow="md"
      opened={isOpen}
      onChange={setOpen}
      withinPortal={true}
      portalProps={{target: 'body'}}
      offset={{
        // Default offset lines up perfectly with the header, which makes it harder to visually interpret
        // the component as a popover.
        mainAxis: 4,
      }}
    >
      <Popover.Target>
        {/* Popover.Target must accept a ref, but Button doesn't, so wrap in a span. */}
        <span>
          <Button
            aria-label="Settings"
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            className={classNames({'is-active': isOpen})}
            icon="gear"
            onClick={() => {
              setOpen(!isOpen);
            }}
            size={size}
          />
        </span>
      </Popover.Target>
      <Popover.Dropdown>
        <AISettingsContent chatId={chatId} />
      </Popover.Dropdown>
    </Popover>
  );
}
